if (!customElements.get('tile-showcase-grid')) {
  customElements.define(
    'tile-showcase-grid',
    class TileShowcaseGrid extends HTMLElement {
      constructor() {
        super();
        this.touchQuery = window.matchMedia('(hover: none)');
        this.onTileClick = this.onTileClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onTileClick);
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onTileClick);
        document.removeEventListener('click', this.onDocumentClick);
      }

      // On touch screens the first tap opens the details; a second tap follows the link.
      onTileClick(event) {
        if (!this.touchQuery.matches) return;
        const tile = event.target.closest('.tile-showcase__tile');
        if (!tile) return;

        if (tile.classList.contains('is-active')) {
          if (tile.tagName !== 'A') this.close(tile);
          return;
        }

        event.preventDefault();
        this.querySelectorAll('.tile-showcase__tile.is-active').forEach((open) => this.close(open));
        tile.classList.add('is-active');
        tile.setAttribute('aria-expanded', 'true');
      }

      onDocumentClick(event) {
        if (this.contains(event.target)) return;
        this.querySelectorAll('.tile-showcase__tile.is-active').forEach((open) => this.close(open));
      }

      close(tile) {
        tile.classList.remove('is-active');
        tile.setAttribute('aria-expanded', 'false');
      }
    }
  );
}

if (!customElements.get('tile-showcase-feature')) {
  customElements.define(
    'tile-showcase-feature',
    class TileShowcaseFeature extends HTMLElement {
      connectedCallback() {
        this.slidesEl = this.querySelector('[data-feature-slides]');
        this.slides = [...this.querySelectorAll('[data-feature-slide]')];
        this.items = [...this.querySelectorAll('[data-feature-item]')];
        this.list = this.querySelector('[data-feature-list]');

        this.addEventListener('click', this.onClick);
        // Theme editor: show the tile being edited.
        this.addEventListener('shopify:block:select', this.onBlockSelect);

        this.querySelectorAll('[data-feature-video]').forEach((video) => {
          video.addEventListener('timeupdate', () => {
            if (video.currentTime >= Number(video.dataset.clipSeconds)) this.endVideo(video);
          });
          video.addEventListener('ended', () => this.endVideo(video));
        });

        this.startAfterIntroLoader();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('shopify:block:select', this.onBlockSelect);
        clearTimeout(this.youtubeTimer);
        if (this.introObserver) this.introObserver.disconnect();
      }

      // The first clip waits for the intro loader, or part of it plays unseen behind it.
      startAfterIntroLoader() {
        const root = document.documentElement;
        if (!root.classList.contains('intro-loader-active')) {
          this.syncMedia();
          return;
        }

        this.introObserver = new MutationObserver(() => {
          if (root.classList.contains('intro-loader-active')) return;
          this.introObserver.disconnect();
          this.syncMedia();
        });
        this.introObserver.observe(root, { attributes: true, attributeFilter: ['class'] });
      }

      onClick = (event) => {
        const thumb = event.target.closest('[data-feature-thumb]');
        if (!thumb) return;
        this.activate(this.items.indexOf(thumb.closest('[data-feature-item]')));

        // Picked from further down the page: bring the large view back into sight.
        if (this.slidesEl.getBoundingClientRect().top < 0) {
          this.slidesEl.scrollIntoView({ behavior: this.scrollBehavior, block: 'start' });
        }
      };

      onBlockSelect = (event) => {
        this.activate(this.items.indexOf(event.target.closest('[data-feature-item]')));
      };

      get scrollBehavior() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      activate(index) {
        if (index < 0 || this.slides[index].classList.contains('is-active')) return;

        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        this.items.forEach((item, i) => {
          item.classList.toggle('is-active', i === index);
          item.querySelector('[data-feature-thumb]').setAttribute('aria-pressed', String(i === index));
        });

        // Centres the picked tile by scrolling the row only, so the page itself doesn't move.
        const item = this.items[index];
        const left = item.offsetLeft - (this.list.clientWidth - item.offsetWidth) / 2;
        this.list.scrollTo({ left, behavior: this.scrollBehavior });

        this.syncMedia();
      }

      // Only the tile on show plays its clip, from the start each time it's picked.
      // Visitors who ask for reduced motion only see the image.
      syncMedia() {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        clearTimeout(this.youtubeTimer);
        this.youtubeRun = null;

        this.slides.forEach((slide) => {
          const play = slide.classList.contains('is-active') && !reduceMotion;
          const video = slide.querySelector('[data-feature-video]');
          const iframe = slide.querySelector('[data-feature-youtube]');

          if (video) {
            if (play) {
              video.classList.remove('is-ended');
              video.currentTime = 0;
              video.muted = true;
              video.play().catch(() => {});
            } else {
              video.pause();
              video.classList.toggle('is-ended', reduceMotion);
            }
          }

          if (iframe) {
            this.stopYoutube(iframe);
            if (play) this.startYoutube(iframe);
          }
        });
      }

      endVideo(video) {
        video.pause();
        video.classList.add('is-ended');
      }

      // The iframe stays invisible until it has played for a few seconds, as YouTube's play
      // button, loading screen and pause button only show around the start of playback.
      // If autoplay is blocked, the image simply stays on show.
      startYoutube(iframe) {
        const run = (this.youtubeRun = {});
        // Starts earlier by the hidden time, so it appears at the chosen start time.
        const start = Math.max(0, Number(iframe.dataset.startSeconds) - TileShowcaseFeature.YOUTUBE_HIDDEN_SECONDS);
        iframe.src = `${iframe.dataset.src}&start=${start}&origin=${encodeURIComponent(window.location.origin)}`;

        TileShowcaseFeature.loadYoutubeApi().then((YT) => {
          if (run !== this.youtubeRun) return;

          const hideCaptions = (player) => {
            try {
              player.unloadModule('captions');
              player.setOption('captions', 'track', {});
            } catch (error) {
              // The captions module isn't always loaded yet; onApiChange tries again.
            }
          };
          let started = false;
          const onPlaying = (player) => {
            if (started || run !== this.youtubeRun) return;
            started = true;
            hideCaptions(player);
            this.youtubeTimer = setTimeout(() => {
              iframe.classList.add('is-loaded');
              this.youtubeTimer = setTimeout(finish, Number(iframe.dataset.clipSeconds) * 1000);
            }, TileShowcaseFeature.YOUTUBE_HIDDEN_SECONDS * 1000);
          };
          // Fade first, then unload so it stops downloading.
          const finish = () => {
            iframe.classList.remove('is-loaded');
            this.youtubeTimer = setTimeout(() => iframe.removeAttribute('src'), 600);
          };

          new YT.Player(iframe, {
            events: {
              onReady: ({ target }) => {
                target.mute();
                hideCaptions(target);
                target.playVideo();
                // Autoplay can begin before the player is listening, which sends no event.
                if (target.getPlayerState() === YT.PlayerState.PLAYING) onPlaying(target);
              },
              onApiChange: ({ target }) => hideCaptions(target),
              onStateChange: ({ data, target }) => {
                if (run !== this.youtubeRun) return;
                if (data === YT.PlayerState.ENDED) {
                  clearTimeout(this.youtubeTimer);
                  finish();
                  return;
                }
                if (data === YT.PlayerState.PLAYING) onPlaying(target);
              },
            },
          });
        });
      }

      static YOUTUBE_HIDDEN_SECONDS = 3;

      static loadYoutubeApi() {
        if (!TileShowcaseFeature.youtubeApi) {
          TileShowcaseFeature.youtubeApi = new Promise((resolve) => {
            if (window.YT && window.YT.Player) {
              resolve(window.YT);
              return;
            }
            const previous = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
              if (previous) previous();
              resolve(window.YT);
            };
            const script = document.createElement('script');
            script.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(script);
          });
        }
        return TileShowcaseFeature.youtubeApi;
      }

      stopYoutube(iframe) {
        iframe.classList.remove('is-loaded');
        iframe.removeAttribute('src');
      }
    }
  );
}
